import { cn } from "@/lib/utils";
import { ReactElement, useEffect, useState } from "react";

export type StreamingIndicatorProps = {
    /** Label d'activité fourni par le serveur (tool calls, etc.). Null = attente silencieuse. */
    label?: string | null;
};

const PENDING_MESSAGES = ["Votre question est en cours de traitement…", "Recherche des informations pertinentes…", "Analyse du contexte…", "Préparation de la réponse…"];

const ROTATION_INTERVAL_MS = 2500;

const TOOL_IDENTIFIER_PATTERN = /([a-z][a-z0-9_]*)__([a-z0-9_]+)/gi;

function formatSnakeCasePart(value: string): string {
    return value
        .split("_")
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

/** `metabase__crm_product_themes` → `Metabase · Crm Product Themes` */
function formatStreamingActivityLabel(label: string): string {
    return label.replace(TOOL_IDENTIFIER_PATTERN, (_, client, toolName) => `${formatSnakeCasePart(client)} · ${formatSnakeCasePart(toolName)}`);
}

export function StreamingIndicator({ label }: StreamingIndicatorProps): ReactElement {
    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        if (label) return;
        const id = setInterval(() => {
            setMessageIndex(i => (i + 1) % PENDING_MESSAGES.length);
        }, ROTATION_INTERVAL_MS);
        return () => clearInterval(id);
    }, [label]);

    useEffect(() => {
        if (!label) setMessageIndex(0);
    }, [label]);

    const displayLabel = label ? formatStreamingActivityLabel(label) : PENDING_MESSAGES[messageIndex];

    return (
        <div className="mt-4 flex items-center">
            <div className="flex items-center justify-center gap-3">
                <div className="relative flex h-3 w-3 rounded-full bg-gray-800">
                    <div className="absolute inset-0 flex h-3 w-3 animate-ping rounded-full bg-gray-600" />
                </div>
                <span className={cn("animate-text-shimmer bg-[length:200%_100%] bg-clip-text text-xs text-transparent", "bg-gradient-to-r from-gray-400 via-gray-700 to-gray-400")}>
                    {displayLabel}
                </span>
            </div>
        </div>
    );
}
